import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import './AuthForms.css';

interface SignUpFormProps {
  onSuccess?: (email: string) => void;
  onSwitchToLogin?: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    givenName: '',
    familyName: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Please create a password';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must include at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must include at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must include at least one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await signUp(
        formData.email,
        formData.password,
        formData.givenName || undefined,
        formData.familyName || undefined
      );
      
      if (onSuccess) {
        onSuccess(formData.email);
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      
      // Handle specific Cognito errors with user-friendly messages
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements. Please check the password rules above.';
      } else if (error.code === 'InvalidParameterException') {
        errorMessage = 'Please check that all information is entered correctly.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <h1 className="auth-title">Create Your Account</h1>
        <p className="auth-subtitle">
          Join My Nerdy Son to get friendly help with all your technology questions
        </p>

        {errors.general && (
          <div className="error-message general-error" role="alert">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address *
            </label>
            <input
              id="email"
              type="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email address"
              required
              autoComplete="email"
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <div id="email-error" className="error-message" role="alert">
                {errors.email}
              </div>
            )}
          </div>

          {/* First Name Field */}
          <div className="form-group">
            <label htmlFor="givenName" className="form-label">
              First Name (Optional)
            </label>
            <input
              id="givenName"
              type="text"
              className="form-input"
              value={formData.givenName}
              onChange={(e) => handleInputChange('givenName', e.target.value)}
              placeholder="Enter your first name"
              autoComplete="given-name"
            />
          </div>

          {/* Last Name Field */}
          <div className="form-group">
            <label htmlFor="familyName" className="form-label">
              Last Name (Optional)
            </label>
            <input
              id="familyName"
              type="text"
              className="form-input"
              value={formData.familyName}
              onChange={(e) => handleInputChange('familyName', e.target.value)}
              placeholder="Enter your last name"
              autoComplete="family-name"
            />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password *
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Create a password"
                required
                autoComplete="new-password"
                aria-describedby="password-requirements password-error"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div id="password-requirements" className="password-requirements">
              <p>Your password must include:</p>
              <ul>
                <li>At least 8 characters</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
              </ul>
            </div>
            {errors.password && (
              <div id="password-error" className="error-message" role="alert">
                {errors.password}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="Enter your password again"
              required
              autoComplete="new-password"
              aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
            />
            {errors.confirmPassword && (
              <div id="confirm-password-error" className="error-message" role="alert">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
            aria-describedby="submit-help"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div id="submit-help" className="form-help">
            By creating an account, you'll be able to save your conversations and preferences.
          </div>
        </form>

        {/* Switch to Login */}
        <div className="auth-switch">
          <p>Already have an account?</p>
          <button
            type="button"
            className="auth-button secondary"
            onClick={onSwitchToLogin}
          >
            Sign In Instead
          </button>
        </div>
      </div>
    </div>
  );
};